import { useCallback } from 'react';
import { type UseFormClearErrors, type UseFormSetError } from 'react-hook-form';
import { useRouter } from 'next/router';

import '@/shared/plan/events.quiz-trial-discount';

import { trackEvent } from '@zep/logger/tracking';

import { getUTMParams } from '@/utils/analytics/utmHelper';

import { quoteDiscountAlert } from '../components/QuoteDiscountAlert';
import { type QuizQuoteFormValues } from '../utils/quizQuote.schema';
import {
  QuizQuoteApiError,
  sendQuizQuoteNotification,
  toQuizQuoteNotificationRequest,
} from '../utils/quizQuoteApi';
import {
  createQuizQuote,
  type FirstPurchasePromotion,
  isTrialEndDiscountApplicable,
  type TrialEndQuoteDiscount,
} from '../utils/quoteCalculator';
import { type QuotePricingPolicy } from '../utils/quotePricingPolicy';
import { quoteResultStorage } from '../utils/quoteResultStorage';

type QuoteSubmitStrategy = (
  values: QuizQuoteFormValues,
) => void | Promise<void>;

/** school 이 돌려주는 에러 필드명 → 폼 필드명. 이름이 다른 것만 적는다. */
const API_FIELD_TO_FORM_FIELD = {
  schoolName: 'school_name',
  accountName: 'account_name',
  accountPhone: 'account_phone',
  accountEmail: 'account_email',
  planType: 'plan_type',
  userCount: 'user_count',
  startDate: 'start_date',
} as const;

/** 이름이 같아 그대로 통과시키는 필드. */
const PASSTHROUGH_FORM_FIELDS = new Set<string>([
  'plan',
  'term',
  'annotation',
  'consent',
]);

function debugQuoteSubmit(message: string, payload?: unknown) {
  if (process.env.NEXT_PUBLIC_STAGE === 'PRD') return;

  // eslint-disable-next-line no-console
  console.debug(`[pricing/quote] ${message}`, payload);
}

/**
 * 할인 모드인데 지금 조건으로는 할인이 안 붙을 때의 확인 (Figma 29113:6734).
 * `다시 선택` 이면 발행을 중단한다.
 */
async function confirmQuoteWithoutDiscount(discount: TrialEndQuoteDiscount) {
  return quoteDiscountAlert.openAsync({ discount });
}

/**
 * school 이 필드별 에러를 주면 해당 입력칸에 붙인다. 하나라도 붙였으면 true.
 * 통짜 메시지만 남기면 사용자가 어느 칸을 고쳐야 하는지 알 수 없다.
 */
function applyServerFieldErrors(
  error: QuizQuoteApiError,
  setError: UseFormSetError<QuizQuoteFormValues>,
): boolean {
  const fields = error.body.error?.fields ?? {};
  let hasFieldError = false;

  Object.entries(fields).forEach(([field, message]) => {
    if (!message) return;

    const formField =
      field in API_FIELD_TO_FORM_FIELD
        ? API_FIELD_TO_FORM_FIELD[field as keyof typeof API_FIELD_TO_FORM_FIELD]
        : PASSTHROUGH_FORM_FIELDS.has(field)
        ? (field as keyof QuizQuoteFormValues)
        : null;
    if (!formField) return;

    hasFieldError = true;
    setError(formField, { type: 'server', message });
  });

  return hasFieldError;
}

export function useQuoteSubmit({
  setError,
  clearErrors,
  policy,
  firstPurchasePromotion = null,
  trialEndDiscount = null,
}: {
  setError: UseFormSetError<QuizQuoteFormValues>;
  clearErrors: UseFormClearErrors<QuizQuoteFormValues>;
  /** 가격 정책. 없으면 금액을 만들 수 없어 발행하지 않는다. */
  policy: QuotePricingPolicy | null;
  firstPurchasePromotion?: FirstPurchasePromotion | null;
  trialEndDiscount?: TrialEndQuoteDiscount | null;
}): QuoteSubmitStrategy {
  const router = useRouter();

  return useCallback<QuoteSubmitStrategy>(
    async values => {
      clearErrors();

      // 1차 방어는 발행 버튼 비활성화다. 여기는 경합(정책 만료 등) 대비 안전망.
      if (!policy) {
        debugQuoteSubmit('pricing policy unavailable');
        setError('root.server', {
          type: 'server',
          message:
            '가격 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
        });
        return;
      }

      const discountApplied = isTrialEndDiscountApplicable(
        trialEndDiscount,
        values,
      );
      if (
        router.query.discount === 'trial' &&
        trialEndDiscount &&
        !discountApplied
      ) {
        const confirmed = await confirmQuoteWithoutDiscount(trialEndDiscount);
        if (!confirmed) return;
      }
      if (trialEndDiscount) {
        trackEvent('quiz_trial_discount_quote_issue', {
          discount_applied: discountApplied,
          user_count: values.user_count,
          term: values.term,
        });
      }

      // 견적서는 클라가 확정한다. school 호출은 신청 통보이며 응답은 쓰지 않는다.
      const quote = createQuizQuote({
        formValues: values,
        policy,
        firstPurchasePromotion,
        trialEndDiscount,
        issuedAt: new Date().toISOString(),
      });

      try {
        const utm = getUTMParams();
        await sendQuizQuoteNotification(
          toQuizQuoteNotificationRequest(values, utm, quote),
        );
        debugQuoteSubmit('quote notification sent');

        let key: string;
        try {
          key = quoteResultStorage.saveAndVerify(quote);
        } catch (error) {
          debugQuoteSubmit('result storage failed', error);
          setError('root.server', {
            type: 'server',
            message:
              '견적 결과를 저장할 수 없습니다. 브라우저 설정을 확인한 후 다시 시도해 주세요.',
          });
          return;
        }

        try {
          await router.push({
            pathname: '/pricing/quote/result',
            query: { key },
          });
        } catch (error) {
          debugQuoteSubmit('result navigation failed', error);
          setError('root.server', {
            type: 'server',
            message:
              '견적 결과는 저장됐지만 결과 화면으로 이동할 수 없습니다. 다시 시도해 주세요.',
          });
        }
      } catch (error) {
        debugQuoteSubmit('submit failed', error);

        if (error instanceof QuizQuoteApiError) {
          if (applyServerFieldErrors(error, setError)) return;

          setError('root.server', { type: 'server', message: error.message });
          return;
        }

        setError('root.server', {
          type: 'server',
          message: '견적서 발행 중 문제가 발생했습니다. 다시 시도해 주세요.',
        });
      }
    },
    [
      clearErrors,
      firstPurchasePromotion,
      policy,
      router,
      setError,
      trialEndDiscount,
    ],
  );
}
